import { useState, type JSX } from "react";

import { FeatureListItem } from "@impero-com/common/components/featureFlipper/featureListItem";
import {
    defaultLocalFeatureFlags,
    getLocalFlagMetadata,
    LocalFeatureFlag,
    localFeatureFlagsAtom,
} from "@impero-com/common/utils/flipper";
import { Button, emitNotification, Divider, Drawer } from "@impero-com/ui";
import { Space, Typography } from "antd";
import { useAtom } from "jotai";

import { ENVIRONMENT } from "../../env";
import { useFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { useFeatureFlagsQuery } from "../../utils/queries/useFeatureFlagsQuery";
import { SHORT_COMMIT_LENGTH } from "../gitInfoWatermark/GitInfoWatermark";

import * as theme from "./theme.css";

const { Title } = Typography;
interface FlippableProps {
    feature: LocalFeatureFlag;
    children: JSX.Element;
}
function Flippable({ feature, children }: FlippableProps) {
    const isEnabled = useFeatureFlag(feature);
    if (isEnabled) {
        return children;
    } else {
        return null;
    }
}

function DebugPanel() {
    const [isOpen, setIsOpen] = useState(false);

    const onOpen = () => setIsOpen(true);
    return (
        <div>
            {!isOpen && <OpenButton onOpen={onOpen} />}
            <DebugPanelBody setIsOpen={setIsOpen} isOpen={isOpen} />
        </div>
    );
}

interface OpenButtonProps {
    onOpen: () => void;
}
function OpenButton({ onOpen }: OpenButtonProps) {
    return (
        <div className={theme.openButton} onClick={onOpen}>
            <span className={theme.openButtonLabel}>Debug</span>
        </div>
    );
}

interface DebugPanelBodyProps {
    setIsOpen: (isOpen: boolean) => void;
    isOpen: boolean;
}
function DebugPanelBody({ isOpen, setIsOpen }: DebugPanelBodyProps) {
    const [flags, setFlags] = useAtom(localFeatureFlagsAtom);

    const { data } = useFeatureFlagsQuery();
    const flagsMetadata = getLocalFlagMetadata(flags);

    const onReset = () => {
        setFlags(defaultLocalFeatureFlags);
    };

    const listItems = flagsMetadata.map((feature) => {
        const { key, description } = feature;
        const isEnabled: boolean = flags[key];
        const onChange = (newIsEnabled: boolean) => {
            setFlags({ ...flags, [key]: newIsEnabled });
        };
        return (
            <FeatureListItem
                key={key}
                featureKey={key}
                description={description}
                isEnabled={isEnabled}
                onChange={onChange}
                defaultEnabled={defaultLocalFeatureFlags[key]}
            />
        );
    });

    // We want to be able to temporarily override remote-configured flags in testing environments, to be able to quickly test different configurations and how they interact.
    // Initially it was in the scope of the feature flipper, but I decided to split that work off, and it may make sense to instead implement in e.g. global
    const remoteFlagListItems = Object.entries(data).map(([key, value]) => {
        return (
            <FeatureListItem
                // We still show reomte-configured flags in the flipper, but the ability to temporarily override them is not fully implemented
                disabled
                key={key}
                featureKey={key}
                isEnabled={value}
                onChange={(newIsEnabled: boolean) => {
                    setFlags({ ...flags, [key]: newIsEnabled });
                }}
                defaultEnabled={value}
            />
        );
    });

    const isLocalFlagsDirty = Object.entries(defaultLocalFeatureFlags).some(
        ([flag, enabled]) => enabled !== flags[flag as LocalFeatureFlag]
    );

    const onCopy = (label: string, value: string | undefined) => () => {
        if (!value) {
            return;
        }
        void navigator.clipboard.writeText(value).then(() => {
            emitNotification("success", `${label} copied`);
        });
    };

    return (
        <Drawer open={isOpen} onOpenChange={setIsOpen} variant="default">
            <div className={theme.drawerBody}>
                <Drawer.Title>Debug panel</Drawer.Title>
                <div>
                    <div>
                        Branch:{" "}
                        {ENVIRONMENT.GIT_BRANCH ? (
                            <a
                                onClick={onCopy(
                                    "Branch",
                                    ENVIRONMENT.GIT_BRANCH
                                )}
                            >
                                {ENVIRONMENT.GIT_BRANCH}
                            </a>
                        ) : (
                            "Unknown"
                        )}
                    </div>
                    <div>
                        Commit:{" "}
                        {ENVIRONMENT.GIT_COMMIT ? (
                            <a
                                onClick={onCopy(
                                    "Commit",
                                    ENVIRONMENT.GIT_COMMIT
                                )}
                            >
                                {ENVIRONMENT.GIT_COMMIT.slice(
                                    0,
                                    SHORT_COMMIT_LENGTH
                                )}
                            </a>
                        ) : (
                            "Unknown"
                        )}
                    </div>
                    {import.meta.env.DEV && (
                        <div>
                            <a
                                href="/api-docs"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                API documentation
                            </a>
                        </div>
                    )}
                </div>
                <Divider />
                <Title level={4}>Testing flags</Title>
                <div className={theme.listItems}>{listItems}</div>
                <div className={theme.actionButtons}>
                    <Space wrap>
                        <Button disabled={!isLocalFlagsDirty} onClick={onReset}>
                            Reset
                        </Button>
                    </Space>
                </div>
                <Divider />
                <Title level={4}>Organization flags</Title>
                These flags can be changed on global per organization. This list
                is shown for debugging purposes.
                <div className={theme.listItems}>{remoteFlagListItems}</div>
            </div>
        </Drawer>
    );
}

export { Flippable, DebugPanel };
